import moment from 'moment';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import UserConstants from 'constants/UserConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {BENCHLING_LOCALE_TAGS} from 'i18n/LanguageTagConstants';
import preferences from 'Preferences';
import {
  CANONICAL_FORMAT,
  USER_EDIT_DATE_FORMAT,
  USER_EDIT_DATETIME_FORMAT,
  USER_EDIT_LOCAL_DATETIME_FORMAT,
  USER_EDIT_TIME_FORMAT,
} from 'util/DateConstants';

import lruMemoize from './lruMemoize';
import {Optional} from './Types';

const {LANGUAGE_PREFERENCE_KEY} = UserConstants;

const PRESENTATIONAL_LOCAL_DATETIME_FORMAT_WITH_TIMEZONE = 'YYYY-MM-DD hh:mm:ss A ZZ'; // What gets copied to clipboard from datetime cell
const INTERNATIONAL_LOCALE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  weekday: 'long',
};

interface DateFormatSettings {
  showYear: boolean;
  showDate: boolean;
  showTime: boolean;
  showWeekday: boolean;
  showSeconds: boolean;
  showTimezoneOffset: boolean;
  languageTag: string;
}

function formatBenchlingCustomDateString(
  date: Date,
  languageTag: string,
  dateOptions?: Intl.DateTimeFormatOptions
): string {
  assert(
    BenchlingConfig.NAV_ENABLE_CUSTOM_LOCALES && BENCHLING_LOCALE_TAGS.hasOwnProperty(languageTag),
    `${languageTag} is not a supported language tag`
  );

  const weekday = dateOptions?.hasOwnProperty('weekday')
    ? date.toLocaleString('en', {weekday: INTERNATIONAL_LOCALE_DATE_FORMAT.weekday})
    : '';
  const day = dateOptions?.hasOwnProperty('day')
    ? date.toLocaleString('en', {day: INTERNATIONAL_LOCALE_DATE_FORMAT.day})
    : '';
  const month = dateOptions?.hasOwnProperty('month')
    ? date.toLocaleString('en', {month: INTERNATIONAL_LOCALE_DATE_FORMAT.month}).toUpperCase()
    : '';
  const year = dateOptions?.hasOwnProperty('year')
    ? date.toLocaleString('en', {year: INTERNATIONAL_LOCALE_DATE_FORMAT.year})
    : '';

  return `${weekday} ${day}${month}${year}`.trim();
}

function formatBenchlingCustomTimeString(date: Date, languageTag: string, showSeconds: boolean): string {
  assert(
    BenchlingConfig.NAV_ENABLE_CUSTOM_LOCALES && BENCHLING_LOCALE_TAGS.hasOwnProperty(languageTag),
    `${languageTag} is not a supported language tag`
  );

  const timeString = date.toLocaleTimeString('en', {
    hour12: languageTag === 'bnch-12hr',
    hour: '2-digit',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
  });

  // Node 22 changed midnight formatting in 24-hour format from "24:00" to "00:00"
  // For bnch-24hr locale, convert "00:00" back to "24:00" to maintain compatibility
  if (languageTag === 'bnch-24hr' && timeString.startsWith('00:')) {
    return timeString.replace(/^00:/, '24:');
  }

  return timeString;
}

function toLocaleDateString(
  date: Date,
  languageTag: string,
  dateOptions?: Intl.DateTimeFormatOptions
): string {
  if (BENCHLING_LOCALE_TAGS.hasOwnProperty(languageTag)) {
    return formatBenchlingCustomDateString(date, languageTag, dateOptions);
  }
  return date.toLocaleDateString(languageTag, dateOptions);
}

const memoizedToLocaleDateString = lruMemoize(
  toLocaleDateString,
  {max: 2000},
  (date, languageTag, dateOptions) => `${date};${languageTag};${JSON.stringify(dateOptions)}`
);

function toLocaleTimeString(date: Date, languageTag: string, showSeconds: boolean): string {
  if (BENCHLING_LOCALE_TAGS.hasOwnProperty(languageTag)) {
    return formatBenchlingCustomTimeString(date, languageTag, showSeconds);
  }
  return date.toLocaleTimeString(languageTag, {
    hour: '2-digit',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
  });
}

const memoizedToLocaleTimeString = lruMemoize(
  toLocaleTimeString,
  {max: 2000},
  (date, languageTag, showSeconds) => `${date};${languageTag};${showSeconds}`
);

function isLocaleSupported(): boolean {
  // Check if browser supports toLocaleDateString
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString#Checking_for_support_for_locales_and_options_arguments
  try {
    // 'i' is an invalid date string, so if the browser properly supports toLocaleDateString,
    // we expect it to throw RangeError
    memoizedToLocaleDateString(new Date(), 'i');
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    if (e.name === 'RangeError') {
      return true;
    }
  }
  return false;
}

export function expectsMonthBeforeDay(languageTag?: Optional<string>): boolean {
  return /en-US/i.test(languageTag || preferences.get(LANGUAGE_PREFERENCE_KEY));
}

function getDefaultDate(
  date: moment.MomentInput,
  {
    showYear,
    showDate,
    showTime,
    showWeekday,
    showSeconds,
    showTimezoneOffset,
    languageTag,
  }: DateFormatSettings
): string {
  const dateFormatString = expectsMonthBeforeDay(languageTag) ? 'M/D' : 'D/M';
  const weekdayFormat = showDate && showWeekday ? 'dddd,' : '';
  const dateFormat = showDate ? dateFormatString : '';
  const yearFormat = showDate && showYear ? '/YYYY' : '';
  const timeFormat = showTime ? `h:mm${showSeconds ? ':ss' : ''}a` : '';

  let format = `${weekdayFormat} ${dateFormat}${yearFormat} ${timeFormat}`;
  if (showTimezoneOffset) {
    format += ' ZZ';
  }
  format = format.trim();

  return moment(date).format(format);
}

/**
 * Formats date into a human readable format
 *
 * Note that if showDate is false, then both showYear and showWeekday should be false (and showTime must be
 * true).
 *
 * And if showTime is false, showSeconds and showTimezoneOffset must also be false
 */
export function readableTime(
  date: moment.MomentInput,
  {
    showYear = true,
    showDate = true,
    showTime = true,
    showWeekday = false,
    showSeconds = false,
    showTimezoneOffset = false,
    languageTag = preferences.get(LANGUAGE_PREFERENCE_KEY),
  }: Partial<DateFormatSettings> = {}
): string {
  const momentDate = moment(date);
  const year = momentDate.year();
  if (year > 9999 || year < 1000 || !momentDate.isValid()) {
    return 'Invalid date';
  }
  const m = momentDate.toDate();
  let dateOptions = {};
  if (showDate) {
    dateOptions = {
      ...dateOptions,
      month: 'numeric',
      day: 'numeric',
    };
    if (showYear) {
      dateOptions = {
        ...dateOptions,
        year: 'numeric',
      };
    }
    if (showWeekday) {
      dateOptions = {
        ...dateOptions,
        weekday: 'long',
      };
    }
  }
  if (!isLocaleSupported()) {
    return getDefaultDate(date, {
      languageTag,
      showYear,
      showDate,
      showTime,
      showWeekday,
      showSeconds,
      showTimezoneOffset,
    });
  }
  let dateString = '';
  try {
    if (showDate) {
      dateString += memoizedToLocaleDateString(m, languageTag, dateOptions);
    }
    if (showTime) {
      if (showDate) {
        dateString += ' ';
      }
      dateString += memoizedToLocaleTimeString(m, languageTag, showSeconds);
      if (showTimezoneOffset) {
        dateString += ` ${momentDate.format('ZZ')}`;
      }
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    // Some foreign browsers error on toLocaleDateString but this isn't caught in the _isLocaleSupported
    // check, so instead we surround this in a general try/catch block to avoid any user-facing errors
    // and fall back to the non-locale-formatted date
    return getDefaultDate(date, {
      languageTag,
      showYear,
      showDate,
      showTime,
      showWeekday,
      showSeconds,
      showTimezoneOffset,
    });
  }
  return dateString;
}

/**
 * Return a "formal" datetime string suitable for human consumption but erring on the side of clarity.
 *x
 * Useful for rendering data in a spreadsheet: "2017-08-30 05:59:59 PM"
 * @param {Date or String} datetime
 * @param {Boolean} includeTimezone (defaults to false)
 */
export function formalTime(datetime: moment.MomentInput, includeTimezone: boolean = false): string {
  return moment(datetime).format(`YYYY-MM-DD hh:mm:ss A${includeTimezone ? ' ZZ' : ''}`);
}

/**
 * Return a "formal" 24 hr UTC datetime string suitable for human consumption.
 *
 * Useful for rendering data in exports: "04 May 2022 15:59:59 UTC"
 * @param {Date or String} datetime
 */
export function formalTimeUTC(datetime: moment.MomentInput): string {
  return `${moment(datetime).utc().format('DD MMM YYYY HH:mm:ss')} UTC`;
}

export function convertUTCDatetimeToLocal(datetime: SchemaTag['value']): SchemaTag['value'] {
  return datetime && moment(datetime, [CANONICAL_FORMAT], true).isValid() ? formalTime(datetime) : datetime;
}

/**
 * Offer some flexibility in the datetime input format, mainly to enable pasting from Excel
 *
 * @return {string[]} list of moment format strings
 */
export function getLocaleInputFormats(
  includeTime: boolean,
  languageTag?: Optional<string>,
  includePresentationalDatetimeWithTimezone: boolean = false
): Array<string> {
  const localeInputFormats = [
    'D MMM YYYY', // 1 Oct 2016
    'D-MMM-YYYY', // 17-Nov-2017
    'D-MMMM-YYYY', // 17-November-2017
    'D.MMM.YYYY', // 17.Nov.2017
    'DMMMYYYY', // 17Nov2017
    'YYYY/M/D', // 2017/11/17
    'YYYY.M.D', // 2017.11.17
    'YYYYMMMDD', // 2023Feb28
    'DDMMMYYYY', // 21Feb2024
  ];

  // allow 1-or-2-digit day/month in correct order
  const [m, d] = expectsMonthBeforeDay(languageTag) ? ['M', 'D'] : ['D', 'M'];

  for (const y of ['YYYY', 'YY']) {
    // allow 2-or-4-digit year
    localeInputFormats.push(`${m}/${d}/${y}`); // time is optional
    localeInputFormats.push(`${m}-${d}-${y}`); // time is optional
    localeInputFormats.push(`${m}.${d}.${y}`); // time is optional
  }

  if (includeTime) {
    localeInputFormats.push('D MMM YYYY h:mm:ss a'); // 1 Oct 2016 9:08:45 AM
    localeInputFormats.push('D-MMM-YYYY h:mm:ss a'); // 17-Nov-2017 10:52:24 AM
    localeInputFormats.push('D-MMMM-YYYY h:mm:ss a'); // 17-November-2017 10:52:24 AM
    localeInputFormats.push('D.MMM.YYYY h:mm:ss a'); // 17.Nov.2017 10:52:24 AM
    localeInputFormats.push(`DMMMYYYY h:mm:ss a`); // 1Oct2016 9:08:45 AM
    localeInputFormats.push(USER_EDIT_LOCAL_DATETIME_FORMAT); // 2020-1-1 1:1:1 AM

    if (includePresentationalDatetimeWithTimezone) {
      localeInputFormats.push(PRESENTATIONAL_LOCAL_DATETIME_FORMAT_WITH_TIMEZONE); // 2020-01-01 01:01:01 AM -0700
    }

    for (const ss of [':ss', '']) {
      // seconds are optional
      for (const a of [' a', '']) {
        // meridiem is optional (user may input 24-hour H)
        for (const y of ['YYYY', 'YY']) {
          // allow 2-or-4-digit year
          localeInputFormats.push(`${m}/${d}/${y} H:mm${ss}${a}`);
          localeInputFormats.push(`${m}-${d}-${y} H:mm${ss}${a}`);
          localeInputFormats.push(`${m}.${d}.${y} H:mm${ss}${a}`);
        }
        localeInputFormats.push(`YYYY/M/D H:mm${ss}${a}`);
        localeInputFormats.push(`YYYY-M-D H:mm${ss}${a}`);
        localeInputFormats.push(`YYYY.M.D H:mm${ss}${a}`);
        localeInputFormats.push(`DDMMMYYYY H:mm${ss}${a}`); // 1Oct2016 9:08:45 AM
      }
    }
  }
  return localeInputFormats;
}

export function isValidDateOrDatetimeOrTime({
  textValue,
  min,
  max,
  validFormats,
}: {
  textValue: string | null;
  min?: string | null;
  max?: string | null;
  validFormats: Array<string>;
}): boolean {
  if (!textValue) {
    return true;
  }
  if (
    !moment(textValue, validFormats, true).isValid() ||
    (min && !moment(min, validFormats, true).isValid()) ||
    (max && !moment(max, validFormats, true).isValid())
  ) {
    return false;
  }

  if (min && moment(textValue, validFormats, true).isBefore(moment(min, validFormats, true))) {
    return false;
  }
  if (max && moment(textValue, validFormats, true).isAfter(moment(max, validFormats, true))) {
    return false;
  }
  return true;
}

export function isValidDate({
  textValue,
  min,
  max,
  validDateFormats = [USER_EDIT_DATE_FORMAT],
}: {
  textValue: string | null;
  min?: string | null;
  max?: string | null;
  validDateFormats?: Array<string>;
}): boolean {
  return isValidDateOrDatetimeOrTime({textValue, min, max, validFormats: validDateFormats});
}

export function isValidDatetime({
  textValue,
  min,
  max,
  validDatetimeFormats = [USER_EDIT_LOCAL_DATETIME_FORMAT],
}: {
  textValue: string;
  min?: string | null;
  max?: string | null;
  validDatetimeFormats?: Array<string>;
}): boolean {
  return isValidDateOrDatetimeOrTime({textValue, min, max, validFormats: validDatetimeFormats});
}

export function isValidTime({
  textValue,
  min,
  max,
  validTimeFormats = [USER_EDIT_TIME_FORMAT],
}: {
  textValue: string;
  min?: string | null;
  max?: string | null;
  validTimeFormats?: Array<string>;
}): boolean {
  return isValidDateOrDatetimeOrTime({textValue, min, max, validFormats: validTimeFormats});
}

export function formatDatetimeValue(
  value: string,
  validInputFormats: Array<string>,
  outputFormat: string,
  defaultValue?: string
): string {
  return value ? moment(value, validInputFormats).format(outputFormat) : (defaultValue ?? value);
}

function _tryParseDate(
  text: string | null,
  languageTag?: Optional<string>,
  includeTime: boolean = false,
  displayDateFormat: string = USER_EDIT_DATE_FORMAT,
  includePresentationalDatetimeWithTimezone: boolean = false
): string | null {
  if (text == null) {
    return null;
  }
  const parsed = moment(
    text,
    [
      USER_EDIT_DATE_FORMAT,
      CANONICAL_FORMAT,
      ...getLocaleInputFormats(includeTime, languageTag, includePresentationalDatetimeWithTimezone),
    ],
    /* strict */ true
  );
  return parsed.isValid() ? parsed.format(displayDateFormat) : null;
}

export const tryParseDate = lruMemoize(
  _tryParseDate,
  {max: 2000},
  (
    text: string | null,
    languageTag?: Optional<string>,
    includeTime: boolean = false,
    displayDateFormat: string = USER_EDIT_DATE_FORMAT,
    includePresentationalDatetimeWithTimezone: boolean = false
  ) =>
    `${text};${languageTag};${includeTime};${displayDateFormat};${includePresentationalDatetimeWithTimezone}}`
);

/**
 * Tries to parse a string date according to the user's languageTag, then formats the date according to
 * the user's languageTag if formatWithUserLocale is true, otherwise formats with ISO format.
 * (Whereas tryParseDate will always format in ISO format).
 * @return {string | null} The date formatted according to the user's languageTag if formatWithUserLocale is true, ISO format if false.
 *                         Returns null if the string input cannot be parsed as a valid date.
 */
export function tryParseAndFormatDateWithUserLanguageTag(
  text: string,
  formatWithUserLocale: boolean
): string | null {
  const languageTag = preferences.get(LANGUAGE_PREFERENCE_KEY);

  const parsedDate = tryParseDate(text, languageTag);
  return tryFormatDate(parsedDate, formatWithUserLocale);
}

/**
 * Tries to parse a string datetime according to the user's languageTag, then formats the datetime according to
 * the user's languageTag if formatWithUserLocale is true, otherwise formats with ISO format.
 * (Whereas tryParseDate will always format in ISO format).
 * @return {string | null} The datetime formatted according to the user's languageTag if formatWithUserLocale is true, ISO format if false.
 *                         Returns null if the string input cannot be parsed as a valid datetime.
 */
export function tryParseAndFormatDatetimeWithUserLanguageTag(
  text: string,
  formatWithUserLocale: boolean
): string | null {
  const languageTag = preferences.get(LANGUAGE_PREFERENCE_KEY);

  const parsedDatetime = tryParseDate(text, languageTag, true, USER_EDIT_DATETIME_FORMAT, true);
  return tryFormatDatetime(parsedDatetime, formatWithUserLocale);
}

function tryFormatDate(parsedDate: string | null, formatWithUserLocale: boolean): string | null {
  if (formatWithUserLocale) {
    return parsedDate ? readableTime(parsedDate, {showTime: false}) : null;
  }
  return parsedDate;
}

function tryFormatDatetime(parsedDatetime: string | null, formatWithUserLocale: boolean): string | null {
  if (formatWithUserLocale) {
    return parsedDatetime ? readableTime(parsedDatetime, {showSeconds: true}) : null;
  }
  return parsedDatetime;
}
